import { cn } from '@/lib/utils';
import { useState, useEffect, useCallback } from "react";
import { TYPOGRAPHY, SPACING, TABS } from '@/design-system';
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LAuth } from "@/components/i18n";
import { logger } from "@/utils/logger";

// Google "G" icon SVG
const GoogleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// LinkedIn icon SVG
const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

interface SocialLoginButtonsProps {
  className?: string;
}

export const SocialLoginButtons = ({ className }: SocialLoginButtonsProps) => {
  const { signInWithGoogle, signInWithLinkedIn } = useAuth();
  const { lAuth } = useLocalizations();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(true);
  const [linkedInEnabled, setLinkedInEnabled] = useState(true);

  // Fetch social login settings
  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['social_login_google_enabled', 'social_login_linkedin_enabled']);

      if (data) {
        data.forEach((setting) => {
          if (setting.setting_key === 'social_login_google_enabled') {
            setGoogleEnabled(setting.setting_value === true);
          }
          if (setting.setting_key === 'social_login_linkedin_enabled') {
            setLinkedInEnabled(setting.setting_value === true);
          }
        });
      }
    };

    fetchSettings();
  }, []);

  const openCenteredPopup = (url: string, name: string, width = 500, height = 600) => {
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    return window.open(
      url,
      name,
      `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`
    );
  };

  const handleOAuthPopup = useCallback(async (
    provider: 'google' | 'linkedin',
    signInFn: typeof signInWithGoogle
  ) => {
    const setLoading = provider === 'google' ? setGoogleLoading : setLinkedInLoading;
    setLoading(true);

    // Open blank popup immediately (within user gesture to avoid blockers)
    const popup = openCenteredPopup('about:blank', `${provider}-signin`);

    try {
      const { error, url } = await signInFn();
      if (error || !url) {
        popup?.close();
        toast.error(lAuth('social.toast.failed', { defaultValue: 'Social login failed' }));
        setLoading(false);
        return;
      }

      if (popup) {
        popup.location.href = url;
      } else {
        // Popup was blocked — fall back to redirect
        window.location.href = url;
        return;
      }

      // Listen for completion message from OAuthCallback
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'oauth-link-complete') {
          window.removeEventListener('message', handleMessage);
          setLoading(false);
          if (event.data.success) {
            // Refresh session to pick up the new auth state
            supabase.auth.getSession();
          } else {
            toast.error(lAuth('social.toast.failed', { defaultValue: 'Social login failed' }));
          }
        }
      };
      window.addEventListener('message', handleMessage);

      // Poll to detect if popup was closed manually
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          setLoading(false);
        }
      }, 500);
    } catch {
      popup?.close();
      toast.error(lAuth('social.toast.failed', { defaultValue: 'Social login failed' }));
      setLoading(false);
    }
  }, [signInWithGoogle, signInWithLinkedIn, lAuth]);

  const handleGoogleLogin = () => handleOAuthPopup('google', signInWithGoogle);
  const handleLinkedInLogin = () => handleOAuthPopup('linkedin', signInWithLinkedIn);

  // If neither provider is enabled, don't render anything
  if (!googleEnabled && !linkedInEnabled) {
    return null;
  }

  return (
    <div className={className}>
      <div className={cn(SPACING.stackMd)}>
        {linkedInEnabled && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleLinkedInLogin}
            disabled={googleLoading || linkedInLoading}
          >
            {linkedInLoading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <LinkedInIcon className="mr-2 h-5 w-5" />
            )}
            <LAuth id="social.continue_with_linkedin" defaultValue='Continue with LinkedIn' />
          </Button>
        )}

        {googleEnabled && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            disabled={googleLoading || linkedInLoading}
          >
            {googleLoading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <GoogleIcon className="mr-2 h-5 w-5" />
            )}
            <LAuth id="social.continue_with_google" defaultValue='Continue with Google' />
          </Button>
        )}
      </div>
      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">
            <LAuth id="social.or_continue_with_email" defaultValue='Or continue with email' />
          </span>
        </div>
      </div>
    </div>
  );
};